import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { keyCodes } from '@/constants';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type FallbackReason = 'network' | 'unknown';

const REASON_TEXT_KEY: Record<FallbackReason, { title: string; message: string }> = {
  network: { title: 'error_network_title', message: 'error_network' },
  unknown: { title: 'error_title', message: 'error_unknown' },
};

export interface FallbackScreenProps {
  reason?: FallbackReason;
  onRetry?: () => void;
}

interface RetryButtonProps {
  message: string;
  onClick: () => void;
}

const RetryButton = ({ message, onClick }: RetryButtonProps) => {
  const { ref, focused, focusSelf } = useExtendFocusable({ isFocusBoundary: true });

  useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  useFocusElement({ ref, focused, onClick, keyCode: keyCodes.ENTER as number, propagate: false });

  return (
    <button ref={ref} type="button" onClick={onClick} className={button}>
      {message}
    </button>
  );
};

const FailIcon = () => (
  <svg className={icon} width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.9202 84H85.0402C88.1202 84 90.0402 80.68 88.5202 78L51.4402 14C49.8802 11.32 46.0402 11.32 44.5202 14L7.44018 78C5.92018 80.68 7.84018 84 10.9202 84ZM52.0002 72H44.0002V64H52.0002V72ZM48.0002 56C45.8002 56 44.0002 54.2 44.0002 52V44C44.0002 41.8 45.8002 40 48.0002 40C50.2002 40 52.0002 41.8 52.0002 44V52C52.0002 54.2 50.2002 56 48.0002 56Z"
      fill="white"
    />
  </svg>
);

export const FallbackScreen = ({ reason = 'unknown', onRetry }: FallbackScreenProps) => {
  const { t } = useTranslation();
  const textKey = REASON_TEXT_KEY[reason];

  return (
    <div className={container}>
      <FailIcon />
      <h1 className={title}>{t(textKey.title)}</h1>
      <p className={message}>{t(textKey.message)}</p>
      {onRetry && <RetryButton message={t('button_retry')} onClick={onRetry} />}
    </div>
  );
};

const container = 'flex flex-col items-center justify-center w-[1920px] h-[1080px] bg-[#0F2648] text-[#FFFDF1]';
const icon = 'w-[96px] h-[96px] mb-[40px]';
const title = 'text-[48px] font-bold text-center mb-[24px]';
const message = 'text-[30px] leading-[1.5] text-center opacity-85 mb-[54px]';
const button =
  'px-[115px] py-[17px] rounded-full bg-[#FFFDF1] text-[#0F2648] text-[30px] font-bold border-[6px] border-transparent focus:border-[#00BFFF] focus:outline-none';
